import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import PublicFooter from "@/components/PublicFooter";
import { COMPANY, LEGAL_EFFECTIVE_DATE, LEGAL_VERSION } from "@/lib/legalVersion";

// Phase P.18 — GDPR — SaaS Terms of Service for JobsBook.app.
// Written in plain, Irish-law-compliant language. Governing law: Ireland.

const sections = [
  {
    n: 1, title: "About these Terms",
    body: (
      <p>
        These Terms of Service (the "Terms") form a binding agreement between{" "}
        <strong>{COMPANY.legal_name}</strong>, a company incorporated in {COMPANY.jurisdiction}{" "}
        (CRO no. {COMPANY.cro_number}, VAT {COMPANY.vat_number}, director {COMPANY.directors})
        with its registered office at {COMPANY.address} ("we", "us", "our"), and the individual
        or entity that creates an account on {COMPANY.trading_name} (the "Service"). By clicking
        "I agree" during sign-up or otherwise using the Service you accept these Terms.
      </p>
    ),
  },
  {
    n: 2, title: "The Service",
    body: (
      <p>
        {COMPANY.trading_name} is a multi-tenant SaaS platform that helps service businesses manage
        bookings, staff scheduling, customer records, invoicing, product stock and ledger. The
        Service is delivered over the public internet on a best-efforts availability basis. We
        do not commit to a formal uptime SLA, but we aim for ≥99.5% monthly availability and
        publish incident notices via email to the account owner.
      </p>
    ),
  },
  {
    n: 3, title: "Account registration & eligibility",
    body: (
      <>
        <p>
          You must be at least 18 years old and legally authorised to bind your business to these
          Terms. You agree to provide accurate, current information at sign-up and to keep it
          up-to-date. You are responsible for the confidentiality of your password and staff PINs
          and for all activity that occurs under your account. Notify us at{" "}
          <a href={`mailto:${COMPANY.contact_email}`} className="text-accent underline">{COMPANY.contact_email}</a>{" "}
          without undue delay of any suspected unauthorised access.
        </p>
      </>
    ),
  },
  {
    n: 4, title: "Subscriptions, fees and refunds",
    body: (
      <>
        <p>
          The Service is offered on a subscription basis. Current plans and pricing are shown at
          sign-up and on our public pricing page. All fees are billed in advance via Stripe. You
          authorise us (through Stripe) to charge your chosen payment method on each renewal
          date until you cancel.
        </p>
        <p className="mt-2">
          <strong>Free trial:</strong> a limited-time trial is offered to new tenants. No card is
          required to start the trial.
        </p>
        <p className="mt-2">
          <strong>Cancellation:</strong> you may cancel at any time via the Stripe Customer
          Portal. Cancellation takes effect at the end of the then-current billing period. Access
          continues to the paid features until then.
        </p>
        <p className="mt-2">
          <strong>Refunds:</strong> we do not offer pro-rated refunds for partial months. If you
          believe you were charged in error, contact {COMPANY.contact_email} within 14 days and
          we will investigate. EU consumers exercising their 14-day withdrawal right under the
          Consumer Rights Directive should note the Service is a digital service — if you request
          immediate access at sign-up you consent to the withdrawal right lapsing once the
          Service begins.
        </p>
      </>
    ),
  },
  {
    n: 5, title: "Acceptable use",
    body: (
      <>
        <p>You agree not to:</p>
        <ul className="mt-2 list-disc pl-5 space-y-1">
          <li>use the Service for any unlawful, fraudulent or infringing purpose;</li>
          <li>upload malware, exploits or otherwise harm the integrity of the Service;</li>
          <li>attempt to bypass tenant isolation, rate limits or authentication controls;</li>
          <li>reverse-engineer, decompile or attempt to derive source code, except where such restriction is prohibited by law;</li>
          <li>scrape or otherwise mass-extract data belonging to another tenant;</li>
          <li>use the Service to send unsolicited marketing or otherwise breach anti-spam legislation, including the Irish ePrivacy Regulations 2011 and the EU ePrivacy Directive.</li>
        </ul>
        <p className="mt-2">
          We may suspend or terminate access without notice if we reasonably believe you have
          breached this section.
        </p>
      </>
    ),
  },
  {
    n: 6, title: "Your data / our data",
    body: (
      <>
        <p>
          <strong>Your business data</strong> — customers, bookings, invoices, staff records,
          notes, uploads — remains your property. You grant us a worldwide, non-exclusive,
          royalty-free licence to host, process, transmit and display this data solely as needed
          to provide the Service and comply with law.
        </p>
        <p className="mt-2">
          <strong>Our platform</strong> — the JobsBook.app software, brand, and documentation —
          remains the intellectual property of {COMPANY.legal_name}. We grant you a
          non-exclusive, non-transferable licence to use the Service in accordance with these
          Terms for the duration of your subscription.
        </p>
      </>
    ),
  },
  {
    n: 7, title: "Privacy and data protection",
    body: (
      <>
        <p>
          Our handling of personal data is described in the{" "}
          <Link to="/privacy" className="text-accent underline">Privacy Policy</Link>. Where you
          upload personal data of your own customers into the Service, you are the controller and
          we act as processor on your behalf. The terms of that processing are set out in our{" "}
          <Link to="/dpa" className="text-accent underline">Data Processing Addendum</Link>, which
          is incorporated into these Terms by reference.
        </p>
      </>
    ),
  },
  {
    n: 8, title: "Third-party services",
    body: (
      <p>
        The Service integrates with third-party services (Stripe, Resend, Google Maps,
        OpenRouteService, MongoDB Atlas, OpenAI). Your use of those services is governed by
        their respective terms. We are not liable for outages or defects of third-party services
        beyond our control.
      </p>
    ),
  },
  {
    n: 9, title: "Warranty disclaimer",
    body: (
      <p className="uppercase text-xs">
        The Service is provided "as is" and "as available". To the maximum extent permitted by
        law, we disclaim all warranties, whether express, implied or statutory, including any
        implied warranties of merchantability, fitness for a particular purpose and
        non-infringement. Nothing in these Terms excludes or limits any statutory right of a
        consumer that cannot be excluded under Irish or EU law.
      </p>
    ),
  },
  {
    n: 10, title: "Limitation of liability",
    body: (
      <>
        <p className="uppercase text-xs">
          To the maximum extent permitted by law, our aggregate liability under or in connection
          with these Terms is limited to the total fees you paid to us in the 12 months
          immediately preceding the event giving rise to the claim. We will not be liable for any
          indirect, incidental, special, consequential or exemplary damages, including loss of
          profits, revenue, goodwill or data.
        </p>
        <p className="mt-2 text-xs">
          Nothing in these Terms limits or excludes liability for (a) death or personal injury
          caused by negligence, (b) fraud or fraudulent misrepresentation, or (c) any other
          liability that cannot be excluded under applicable Irish law.
        </p>
      </>
    ),
  },
  {
    n: 11, title: "Suspension & termination",
    body: (
      <>
        <p>
          Either party may terminate the subscription for any reason on notice as described in
          Section 4. We may terminate or suspend immediately if you materially breach these Terms
          (including non-payment), if we are required to do so by law, or if continuing the
          Service creates a security risk to other tenants.
        </p>
        <p className="mt-2">
          On termination, your access ends and we will retain your data for the retention windows
          set out in the Privacy Policy so that you can export it before deletion. You may
          request earlier erasure via <em>Settings → Privacy &amp; data → Delete my account</em>.
        </p>
      </>
    ),
  },
  {
    n: 12, title: "Changes to the Service or these Terms",
    body: (
      <p>
        We may modify the Service (adding, changing or removing features) at any time. We will
        give at least 30 days' notice by email of any material change to these Terms or to
        pricing. If you do not agree with the change you may cancel your subscription before it
        takes effect. Continued use after the effective date constitutes acceptance.
      </p>
    ),
  },
  {
    n: 13, title: "Governing law and jurisdiction",
    body: (
      <p>
        These Terms are governed by the laws of {COMPANY.jurisdiction}, without regard to
        conflict-of-laws principles. The courts of {COMPANY.jurisdiction} have exclusive
        jurisdiction over any dispute arising out of or in connection with these Terms, subject
        to your non-waivable statutory rights as a consumer to bring proceedings in the courts
        of your Member State of residence.
      </p>
    ),
  },
  {
    n: 14, title: "Contact",
    body: (
      <p>
        Questions about these Terms? Email{" "}
        <a href={`mailto:${COMPANY.contact_email}`} className="text-accent underline">{COMPANY.contact_email}</a> or write to
        {" "}{COMPANY.legal_name}, {COMPANY.address}.
      </p>
    ),
  },
];

export default function Terms() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="terms-page">
      <div className="border-b border-border">
        <div className="max-w-[820px] mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm font-bold" data-testid="terms-back-home">
            <ArrowLeft className="h-4 w-4" /> Back to JobsBook
          </Link>
          <span className="mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Legal</span>
        </div>
      </div>

      <main className="max-w-[820px] mx-auto px-5 md:px-8 py-12">
        <div className="mono text-[11px] uppercase tracking-[0.3em] text-accent">// {COMPANY.trading_name}</div>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2">Terms of Service</h1>
        <div className="mt-2 text-sm text-muted-foreground">
          Effective: <span className="mono">{LEGAL_EFFECTIVE_DATE}</span>{" "}
          · Version <span className="mono">{LEGAL_VERSION}</span>
        </div>

        <div className="mt-8 space-y-8">
          {sections.map((s) => (
            <section key={s.n} data-testid={`terms-section-${s.n}`}>
              <h2 className="text-lg font-extrabold tracking-tight">{s.n}. {s.title}</h2>
              <div className="mt-2 text-sm leading-relaxed text-foreground/85 space-y-2">
                {s.body}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t border-border">
          <PublicFooter tone="dark" />
        </div>
      </main>
    </div>
  );
}
